import { Text } from "@ui-kitten/components";
import { SeatDetails, TicketCartDetails } from "features/types";
import React, { useMemo } from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";

type Props = {
  seats: SeatDetails[];
  charge: TicketCartDetails | null;
  isLoading: boolean;
  parkingSpacesCount?: number;
  parkingSectionLabel?: string;
  parkingSelections?: Array<{ section: string; row: string; seat: string }>;
};

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const SeasonPlanOrderSummary = ({
  seats,
  charge,
  isLoading,
  parkingSpacesCount,
  parkingSectionLabel,
  parkingSelections,
}: Props) => {
  const seatsByRow = useMemo(() => {
    const grouped: Record<string, SeatDetails[]> = {};
    for (const seat of seats) {
      const sectionKey = seat.sectionDisplay || seat.section;
      const rowKey = seat.rowDisplay || seat.row;
      const key = `${sectionKey}||${rowKey}`;
      (grouped[key] ??= []).push(seat);
    }
    return Object.entries(grouped).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [seats]);

  const subtotal = useMemo(() => {
    if (!charge) return 0;
    return (charge.items ?? []).reduce(
      (sum, item) =>
        sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice),
      0,
    );
  }, [charge]);

  const parkingSubtotal = useMemo(() => {
    if (!charge?.items || !parkingSelections || parkingSelections.length === 0)
      return 0;
    const keys = new Set(
      parkingSelections.map(
        (s) =>
          `${s.section}`.toUpperCase() +
          `~${s.row}`.toUpperCase() +
          `~${s.seat}`.toUpperCase(),
      ),
    );
    return charge.items.reduce((sum, item) => {
      const key =
        `${item.section}`.toUpperCase() +
        `~${item.row}`.toUpperCase() +
        `~${item.seat}`.toUpperCase();
      if (!keys.has(key)) return sum;
      return sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice);
    }, 0);
  }, [charge?.items, parkingSelections]);

  const seatsSubtotal = useMemo(() => {
    const value = subtotal - parkingSubtotal;
    return value >= 0 ? value : 0;
  }, [subtotal, parkingSubtotal]);

  const taxesAndFees = useMemo(() => {
    if (!charge?.feeItems) return 0;
    return charge.feeItems.reduce(
      (sum, item) =>
        sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice),
      0,
    );
  }, [charge]);

  const total = useMemo(() => {
    if (!charge) return subtotal + taxesAndFees;
    const byField = toNumber(
      charge.totalCreditCardCharges ?? charge.totalPrice,
    );
    return byField || subtotal + taxesAndFees;
  }, [charge, subtotal, taxesAndFees]);

  return (
    <View
      className="py-4 px-6 rounded-xl h-full"
      style={{ backgroundColor: "#ececec" }}
    >
      <View className="border-b-4 border-b-slate-900 pb-2">
        <Text category="h1" style={{ textAlign: "center" }}>
          ORDER SUMMARY
        </Text>
      </View>

      <View className="mt-4">
        <View className="flex-row justify-between">
          <span>Seats selected:</span>
          <span className="font-bold">{seats.length}</span>
        </View>
        {parkingSpacesCount !== undefined && (
          <View className="mt-2 gap-1">
            <View className="flex-row justify-between">
              <span>Parking spaces:</span>
              <span className="font-bold">{parkingSpacesCount}</span>
            </View>
            {parkingSectionLabel && (
              <View className="flex-row justify-between">
                <span>Parking section:</span>
                <span className="font-bold">{parkingSectionLabel}</span>
              </View>
            )}
            {parkingSpacesCount > 0 && (
              <Text appearance="hint" category="c1">
                Parking selection is final. Clear cart to restart.
              </Text>
            )}
          </View>
        )}

        {seatsByRow.length > 0 && (
          <View className="mt-3">
            <span className="text-xs uppercase tracking-wide text-gray-600">
              Selected Seats
            </span>
            <View className="mt-2 gap-1">
              {seatsByRow.map(([groupKey, rowSeats]) => {
                const [sectionName, rowName] = groupKey.split("||");
                const seatList = [...rowSeats]
                  .sort((a, b) =>
                    (a.seatDisplay || a.seat).localeCompare(
                      b.seatDisplay || b.seat,
                      undefined,
                      { numeric: true },
                    ),
                  )
                  .map((s) => s.seatDisplay || s.seat)
                  .join(", ");
                return (
                  <View key={groupKey}>
                    <span className="text-sm text-gray-700">
                      section: {sectionName}, row: {rowName}, seat(s):{" "}
                      {seatList}
                    </span>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        <View className="mt-4 pt-4 border-t border-gray-400/40">
          {isLoading && (
            <Text appearance="hint" category="c1">
              Calculating…
            </Text>
          )}

          <View className="mt-2 gap-1">
            {parkingSpacesCount !== undefined &&
              parkingSpacesCount > 0 &&
              charge && (
                <>
                  <View className="flex-row justify-between">
                    <span>Seats</span>
                    <span className="font-bold">
                      {formatCurrency(seatsSubtotal)}
                    </span>
                  </View>
                  <View className="flex-row justify-between">
                    <span>Parking</span>
                    <span className="font-bold">
                      {formatCurrency(parkingSubtotal)}
                    </span>
                  </View>
                  <View className="flex-row justify-between">
                    <span>Subtotal</span>
                    <span className="font-bold">
                      {formatCurrency(charge ? subtotal : 0)}
                    </span>
                  </View>
                </>
              )}
            {(!parkingSpacesCount || parkingSpacesCount === 0) && (
              <View className="flex-row justify-between">
                <span>Subtotal</span>
                <span className="font-bold">
                  {formatCurrency(charge ? subtotal : 0)}
                </span>
              </View>
            )}
            <View className="flex-row justify-between">
              <span>Taxes &amp; fees</span>
              <span className="font-bold">
                {formatCurrency(charge ? taxesAndFees : 0)}
              </span>
            </View>
            <View className="flex-row justify-between">
              <span className="font-bold">Total</span>
              <span className="font-bold">
                {formatCurrency(charge ? total : 0)}
              </span>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default SeasonPlanOrderSummary;
